<template>
  <div class="relative w-full overflow-hidden pb-8 sm:pb-12 lg:pb-20">
    <!-- No local background color here, inherits from parent -->

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <div class="flex flex-col lg:flex-row gap-6 lg:gap-14 items-stretch">

        <!-- LEFT: Image Panel -->
        <div class="w-full lg:w-[58%] relative">
          <div class="relative w-full h-[320px] sm:h-[400px] lg:h-[520px] rounded-2xl overflow-hidden border border-white/60 bg-white shadow-xl shadow-blue-900/10">
            <TransitionGroup name="stack">
              <div
                v-for="(item, index) in items"
                :key="item.id"
                v-show="activeIndex === index"
                class="absolute inset-0 w-full h-full transition-all duration-1000"
              >
                <!-- Video -->
                <div v-if="item.video_url && isVideo(item.video_url)" class="w-full h-full bg-slate-900">
                  <iframe
                    :src="getEmbedUrl(item.video_url)"
                    class="w-full h-full border-0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen
                  ></iframe>
                </div>
                <!-- Image -->
                <img
                  v-else
                  :src="getSafeImageUrl(currentGalleryImage || item.image_url)"
                  class="w-full h-full object-cover transition-all duration-700"
                  :alt="item.title"
                />

                <!-- Bottom gradient -->
                <div class="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-slate-900/10 to-transparent pointer-events-none"></div>

                <!-- Badge -->
                <div class="absolute top-4 left-4">
                  <span class="px-3 py-1 bg-blue-600/90 backdrop-blur-md text-white text-[10px] sm:text-xs font-black uppercase tracking-[0.2em] rounded-full border border-white/20 shadow-lg">
                    {{ item.badge || 'Showcase' }}
                  </span>
                </div>

                <!-- Thumbnail strip - Compact -->
                <div
                  v-if="item.extra_images && item.extra_images.length > 0"
                  class="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 right-3 sm:right-4 flex gap-2 overflow-x-auto no-scrollbar py-1"
                >
                  <div
                    class="w-10 h-10 sm:w-12 sm:h-12 rounded-lg border-2 overflow-hidden cursor-pointer flex-shrink-0 transition-all duration-200"
                    :class="currentGalleryImage === null ? 'border-blue-500 scale-105 shadow-lg' : 'border-white/40 opacity-90 hover:opacity-100'"
                    @click="currentGalleryImage = null"
                  >
                    <img :src="getSafeImageUrl(item.image_url)" class="w-full h-full object-cover" />
                  </div>
                  <div
                    v-for="(img, idx) in item.extra_images"
                    :key="idx"
                    class="w-10 h-10 sm:w-12 sm:h-12 rounded-lg border-2 overflow-hidden cursor-pointer flex-shrink-0 transition-all duration-200"
                    :class="currentGalleryImage === img ? 'border-blue-500 scale-105 shadow-lg' : 'border-white/40 opacity-90 hover:opacity-100'"
                    @click="currentGalleryImage = img"
                  >
                    <img :src="getSafeImageUrl(img)" class="w-full h-full object-cover" />
                  </div>
                </div>
              </div>
            </TransitionGroup>
          </div>

          <!-- Progress Bar - Cleaner -->
          <div class="mt-4 sm:mt-5 h-[3px] bg-blue-200/50 rounded-full overflow-hidden max-w-[150px] sm:max-w-[200px]">
            <div
              class="h-full bg-blue-600 rounded-full transition-all duration-500 ease-out"
              :style="{ width: `${((activeIndex + 1) / items.length) * 100}%` }"
            ></div>
          </div>
        </div>

        <!-- RIGHT: Content & Navigation -->
        <div class="w-full lg:w-[42%] flex flex-col justify-between py-2 sm:py-6 lg:py-6">
          <Transition name="fade-content" mode="out-in">
            <div :key="activeIndex" class="space-y-4 sm:space-y-6">
              <!-- Location Tag -->
              <div class="flex items-center gap-2 sm:gap-3 text-blue-600 font-bold uppercase tracking-[0.2em] text-[9px] sm:text-[10px]">
                <span class="w-4 sm:w-6 h-[1px] bg-blue-500 flex-shrink-0"></span>
                {{ items[activeIndex]?.location || 'Technomac Snc' }}
              </div>

              <!-- Title -->
              <h3 class="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-tight text-slate-900">
                {{ items[activeIndex]?.title }}
              </h3>

              <!-- Description -->
              <p class="text-slate-600 text-sm sm:text-base font-medium leading-relaxed">
                {{ items[activeIndex]?.description }}
              </p>

              <!-- Meta tags -->
              <div class="flex flex-wrap gap-2 pt-1 sm:pt-2">
                <span
                  v-for="tag in (items[activeIndex]?.tags || []).slice(0, 3)"
                  :key="tag"
                  class="px-3 py-1 bg-white border border-blue-100 text-blue-700 text-[10px] sm:text-xs rounded-full shadow-sm font-semibold"
                >
                  {{ tag }}
                </span>
              </div>
            </div>
          </Transition>

          <!-- Navigation Controls - Refined -->
          <div class="flex flex-wrap items-center justify-between gap-4 mt-8 lg:mt-0 pt-4 sm:pt-0 border-t sm:border-0 border-blue-100/50">
            <div class="flex items-center gap-2">
              <button
                @click="prev"
                class="w-12 h-12 sm:w-11 sm:h-11 rounded-full border border-blue-100 bg-white shadow-sm hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all duration-300 flex items-center justify-center flex-shrink-0 group"
                aria-label="Previous"
              >
                <i class="fas fa-chevron-left text-blue-600 group-hover:text-white text-sm"></i>
              </button>
              <button
                @click="next"
                class="w-12 h-12 sm:w-11 sm:h-11 rounded-full border border-blue-100 bg-white shadow-sm hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all duration-300 flex items-center justify-center flex-shrink-0 group"
                aria-label="Next"
              >
                <i class="fas fa-chevron-right text-blue-600 group-hover:text-white text-sm"></i>
              </button>
            </div>

            <!-- Counter -->
            <div class="text-slate-400 text-[10px] sm:text-xs tracking-widest uppercase flex items-center gap-1 sm:gap-2">
              <span class="text-slate-900 font-black text-xs sm:text-sm">{{ String(activeIndex + 1).padStart(2, '0') }}</span>
              <span class="opacity-30">/</span>
              {{ String(items.length).padStart(2, '0') }}
            </div>

            <!-- Dot Indicators -->
            <div class="flex gap-1.5 items-center">
              <span
                v-for="(_, i) in items"
                :key="i"
                @click="activeIndex = i; resetTimer()"
                :class="[
                  'h-1.5 rounded-full transition-all duration-500 cursor-pointer',
                  activeIndex === i ? 'w-6 sm:w-8 bg-blue-600' : 'w-1.5 bg-blue-200 hover:bg-blue-300'
                ]"
              ></span>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  items: {
    type: Array,
    default: () => []
  }
});

const activeIndex = ref(0);
const currentGalleryImage = ref(null);
const { getSafeImageUrl } = useImageHandling();

watch(activeIndex, () => {
  currentGalleryImage.value = null;
});

const next = () => {
  activeIndex.value = (activeIndex.value + 1) % props.items.length;
};

const prev = () => {
  activeIndex.value = (activeIndex.value - 1 + props.items.length) % props.items.length;
};

const isVideo = (url) => {
  if (!url) return false;
  return url.includes('youtube.com') || url.includes('youtu.be') || url.includes('vimeo.com');
};

const getEmbedUrl = (url) => {
  if (url.includes('youtube.com/watch?v=')) return url.replace('watch?v=', 'embed/');
  if (url.includes('youtu.be/')) return url.replace('youtu.be/', 'www.youtube.com/embed/');
  return url;
};

// Autoplay
let timer = null;
const resetTimer = () => {
  clearInterval(timer);
  timer = setInterval(next, 8000);
};

onMounted(() => {
  timer = setInterval(next, 8000);
});
onUnmounted(() => {
  clearInterval(timer);
});
</script>

<style scoped>
/* Stack Transition */
.stack-enter-active,
.stack-leave-active {
  transition: all 0.9s cubic-bezier(0.16, 1, 0.3, 1);
}
.stack-enter-from {
  opacity: 0;
  transform: scale(1.05) translateX(30px);
}
.stack-leave-to {
  opacity: 0;
  transform: scale(0.95) translateX(-30px);
}

/* Content Fade */
.fade-content-enter-active,
.fade-content-leave-active {
  transition: all 0.5s ease-out;
}
.fade-content-enter-from {
  opacity: 0;
  transform: translateY(15px);
}
.fade-content-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.no-scrollbar::-webkit-scrollbar { display: none; }
.no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
</style>
